import { Autocomplete, AutocompleteProps, TextField } from "@mui/material";
import { AssetStatus, BusinessEntity } from "../../../../../graphql/generated";
import { ConditionValueSelectorComponentProps } from "../../ConditionForm";
import { castArray } from "lodash";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { testIds } from "./BusinessEntityInput.testIds";
import activeOrSelected from "../../../../../utils/assets/activeOrSelected";

type BusinessEntityOption = Pick<BusinessEntity, "_id" | "name" | "status">;

const BusinessEntityInput = ({
  value,
  onChange,
  businessEntities,
  onSearchChange,
  sx,
  ...otherProps
}: ConditionValueSelectorComponentProps & {
  businessEntities: BusinessEntityOption[];
  onSearchChange?: (search: string) => void;
} & Pick<
    AutocompleteProps<BusinessEntityOption | string, true, undefined, undefined>,
    "sx"
  >) => {
  const { t } = useTranslation("common");
  const [inputValue, setInputValue] = useState("");
  const selectableBusinessEntities = activeOrSelected(
    businessEntities,
    castArray(value ?? []).filter((id): id is string => typeof id === "string")
  );
  const getBusinessEntityLabel = (
    businessEntity: BusinessEntityOption | string
  ) => {
    if (typeof businessEntity === "string") {
      return businessEntity;
    }
    return businessEntity.status === AssetStatus.Inactive
      ? `${businessEntity.name} (${t("statusTypes.INACTIVE")})`
      : businessEntity.name;
  };

  return (
    <Autocomplete
      data-testid={testIds.businessEntityInputAutocomplete}
      disablePortal
      options={selectableBusinessEntities}
      getOptionLabel={getBusinessEntityLabel}
      renderOption={(props, businessEntity) => (
        <li
          {...props}
          data-testid={testIds.businessEntityInputAutocompleteOption}
          data-value={
            typeof businessEntity === "string"
              ? businessEntity
              : businessEntity._id
          }
        >
          {getBusinessEntityLabel(businessEntity)}
        </li>
      )}
      onChange={(event, value) => {
        if (!value) {
          onChange([]);
          return;
        }
        onChange(
          value
            .map((be) => (typeof be === "string" ? undefined : be._id))
            .filter(Boolean) as string[]
        );
      }}
      value={
        selectableBusinessEntities.filter((be) =>
          castArray(value).includes(be._id)
        ) || []
      }
      size="small"
      renderInput={(inputProps) => (
        <TextField
          {...inputProps}
          inputProps={{
            ...inputProps.inputProps,
            "data-testid": testIds.businessEntityInputAutocompleteInput,
          }}
        />
      )}
      multiple
      freeSolo
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        onSearchChange?.(newInputValue);
      }}
      filterOptions={(options, params) => {
        // Return all options since filtering is handled server-side
        return options;
      }}
      sx={[
        {
          minWidth: 300,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...otherProps}
    />
  );
};

export default BusinessEntityInput;
